import React, { useState, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import axios from 'axios'
import * as XLSX from 'xlsx'
import { Grid, GridColumn } from '@progress/kendo-react-grid'
import { orderBy, filterBy } from '@progress/kendo-data-query'
import { DatePicker } from '@progress/kendo-react-dateinputs'
import { Button } from '@progress/kendo-react-buttons'
import { ComboBox } from '@progress/kendo-react-dropdowns'
import { FaFileExcel, FaSearch } from 'react-icons/fa'
import { getCurrentUserEmployee } from '../../data/employeeData'
import { getDepartmentDropdown } from '../../data/departmentData'
import { useToast } from '../../contexts/ToastContext'
import './InvoicesReport.css'

interface InvoiceReportData {
  invoiceId: number
  ref: string
  refFormatted: string
  invoiceDate: string
  client: string
  site: string
  value: number
  isPaid: boolean
  paymentDate: string | null
}

function InvoicesReport() {
  const navigate = useNavigate()
  const { showToast } = useToast()
  
  // Date range
  const [startDate, setStartDate] = useState<Date | null>(new Date(new Date().getFullYear(), 0, 1))
  const [endDate, setEndDate] = useState<Date | null>(new Date())
  
  // Data and filters
  const [data, setData] = useState<InvoiceReportData[]>([])
  const [loading, setLoading] = useState(false)
  const [hasPermission, setHasPermission] = useState(false)
  const [departments, setDepartments] = useState<any[]>([])
  const [selectedDepartment, setSelectedDepartment] = useState<any>(null)
  
  // Grid state
  const [sort, setSort] = useState<any[]>([])
  const [filter, setFilter] = useState<any>(null)
  const [skip, setSkip] = useState(0)
  const [pageSize] = useState(25)

  const apiURL = process.env.REACT_APP_API_URL

  // Fetch departments
  useEffect(() => {
    const fetchDepartments = async () => {
      try {
        const depts = await getDepartmentDropdown()
        setDepartments([{ value: null, text: 'All Departments' }, ...depts])
      } catch (error) {
        console.error('Error fetching departments:', error)
      }
    }
    fetchDepartments()
  }, [])

  // Check permissions
  useEffect(() => {
    const checkPermissions = async () => {
      try {
        const currentUser = await getCurrentUserEmployee()
        if (currentUser && currentUser.permissions) {
          const permitted = currentUser.permissions.some(
            (p) => p.area === 'Reports' && p.permission === 'Invoices' && p.assigned
          )
          setHasPermission(permitted)
          if (!permitted) {
            showToast('You do not have permission to access this page', 'error')
            navigate('/')
          }
        } else {
          showToast('Unable to verify permissions', 'error')
          navigate('/')
        }
      } catch (error) {
        console.error('Error checking permissions:', error)
        showToast('Error verifying permissions', 'error')
        navigate('/')
      }
    }
    checkPermissions()
  }, [navigate, showToast])

  // Fetch invoice data
  const fetchData = useCallback(async () => {
    if (!startDate || !endDate) {
      showToast('Please select both start and end dates', 'info')
      return
    }

    if (startDate > endDate) {
      showToast('Start date must be before end date', 'error')
      return
    }

    try {
      setLoading(true)
      const token = localStorage.getItem('authToken')
      
      if (!token) {
        throw new Error('Authorization token not found')
      }

      const startDateStr = startDate.toISOString().split('T')[0]
      const endDateStr = endDate.toISOString().split('T')[0]
      
      let url = `https://${apiURL}/Invoice?fromdate=${startDateStr}&todate=${endDateStr}`
      
      if (selectedDepartment && selectedDepartment.value !== null) {
        url += `&departmentid=${selectedDepartment.value}`
      }

      console.log('Fetching invoice report from:', url)

      const response = await axios.get<InvoiceReportData[]>(url, {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': 'Bearer ' + token,
        },
      })

      const invoices = response.data
      setData(invoices)
      showToast(`Loaded ${invoices.length} invoices`, 'success')
    } catch (error: any) {
      console.error('Error fetching invoice data:', error)
      const errorMessage = error.response?.data?.message || error.message || 'Error loading invoice data'
      showToast(errorMessage, 'error')
    } finally {
      setLoading(false)
    }
  }, [startDate, endDate, selectedDepartment, showToast, apiURL])

  // Prepare data for grid
  const dataFiltered = filter ? filterBy(data, filter) : data
  const dataSorted = orderBy(dataFiltered, sort)

  // Export to Excel
  const exportToExcel = () => {
    const exportData = dataSorted.map(row => ({
      'Invoice Ref': row.refFormatted,
      'Invoice Date': new Date(row.invoiceDate).toLocaleDateString('en-GB'),
      'Client': row.client,
      'Site': row.site,
      'Total Value': Number(row.value),
      'Paid': row.isPaid ? 'Yes' : 'No'
    }))

    const worksheet = XLSX.utils.json_to_sheet(exportData)
    const workbook = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Invoices')
    
    const fileName = `invoices_report_${startDate?.toISOString().split('T')[0]}_to_${endDate?.toISOString().split('T')[0]}.xlsx`
    XLSX.writeFile(workbook, fileName)
    
    showToast(`Exported ${exportData.length} invoices to ${fileName}`, 'success')
  }

  // Calculate totals
  const totalValue = dataSorted.reduce((sum, invoice) => sum + invoice.value, 0)
  const paidValue = dataSorted.filter(inv => inv.isPaid).reduce((sum, invoice) => sum + invoice.value, 0)
  const unpaidValue = totalValue - paidValue

  return (
    <div>
      <div style={{ padding: "1rem" }}>
        <div className="invoices-report__header">
          <h1>Invoices Report</h1>
        </div>

        {/* Date Range and Filters */}
        <div style={{ 
          background: '#f5f9fb', 
          padding: '1.5rem', 
          borderRadius: '8px', 
          marginTop: '1rem',
          marginBottom: '1rem'
        }}>
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
            gap: '1rem',
            alignItems: 'end'
          }}>
            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold', color: '#1e2a4e' }}>
                Start Date:
              </label>
              <DatePicker
                value={startDate}
                onChange={(e) => setStartDate(e.value)}
                format="dd/MM/yyyy"
                style={{ width: '100%' }}
              />
            </div>
            
            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold', color: '#1e2a4e' }}>
                End Date:
              </label>
              <DatePicker
                value={endDate}
                onChange={(e) => setEndDate(e.value)}
                format="dd/MM/yyyy"
                style={{ width: '100%' }}
              />
            </div>

            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold', color: '#1e2a4e' }}>
                Department:
              </label>
              <ComboBox
                data={departments}
                textField="text"
                dataItemKey="value"
                value={selectedDepartment}
                onChange={(e) => setSelectedDepartment(e.value)}
                placeholder="Select department..."
                style={{ width: '100%' }}
              />
            </div>

            <div>
              <Button
                onClick={fetchData}
                icon="search"
                themeColor="primary"
                disabled={loading || !hasPermission}
                style={{ width: '100%', height: '40px' }}
              >
                <FaSearch style={{ marginRight: "0.5rem" }} />
                {loading ? 'Loading...' : 'Run Report'}
              </Button>
            </div>
          </div>
        </div>

        {/* Summary Cards and Export Button */}
        {data.length > 0 && (
          <div>
            <div style={{ display: "flex", flexDirection: "row", justifyContent: "flex-end", marginBottom: "1rem" }}>
              <Button
                onClick={exportToExcel}
                icon="excel"
                themeColor="primary"
                data-excel="true"
              >
                <FaFileExcel style={{ marginRight: "0.5rem" }} />
                Export to Excel
              </Button>
            </div>
            
            <div style={{ 
              display: 'grid', 
              gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
              gap: '1rem',
              marginBottom: '1.5rem'
            }}>
            <div style={{ 
              background: '#1e2a4e', 
              color: 'white', 
              padding: '1rem', 
              borderRadius: '8px',
              textAlign: 'center'
            }}>
              <div style={{ fontSize: '0.9rem', marginBottom: '0.5rem' }}>Total Invoices</div>
              <div style={{ fontSize: '1.8rem', fontWeight: 'bold' }}>{dataSorted.length}</div>
            </div>
            
            <div style={{ 
              background: '#aec741', 
              color: '#475315', 
              padding: '1rem', 
              borderRadius: '8px',
              textAlign: 'center'
            }}>
              <div style={{ fontSize: '0.9rem', marginBottom: '0.5rem' }}>Total Value</div>
              <div style={{ fontSize: '1.8rem', fontWeight: 'bold' }}>
                £{totalValue.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </div>
            </div>

            <div style={{ 
              background: '#d4edda', 
              color: '#155724', 
              padding: '1rem', 
              borderRadius: '8px',
              textAlign: 'center'
            }}>
              <div style={{ fontSize: '0.9rem', marginBottom: '0.5rem' }}>Paid</div>
              <div style={{ fontSize: '1.8rem', fontWeight: 'bold' }}>
                £{paidValue.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </div>
            </div>

            <div style={{ 
              background: '#f8d7da', 
              color: '#721c24', 
              padding: '1rem', 
              borderRadius: '8px',
              textAlign: 'center'
            }}>
              <div style={{ fontSize: '0.9rem', marginBottom: '0.5rem' }}>Unpaid</div>
              <div style={{ fontSize: '1.8rem', fontWeight: 'bold' }}>
                £{unpaidValue.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </div>
            </div>
          </div>
          </div>
        )}

        {/* Grid */}
        {data.length > 0 && (
          <div className="invoices-report-grid">
            <Grid
              data={dataSorted.slice(skip, skip + pageSize)}
              sortable
              sort={sort}
              onSortChange={(e) => setSort(e.sort)}
              filterable
              filter={filter}
              onFilterChange={(e) => {
                setFilter(e.filter)
                setSkip(0)
              }}
              pageable
              pageSize={pageSize}
              skip={skip}
              onPageChange={(e) => setSkip(e.page.skip)}
              total={dataFiltered.length}
              style={{ height: '600px', width: '100%' }}
            >
              <GridColumn field="refFormatted" title="Invoice Ref" width="150px" />
              <GridColumn 
                field="invoiceDate" 
                title="Invoice Date" 
                width="150px"
                cell={(props) => (
                  <td>{new Date(props.dataItem.invoiceDate).toLocaleDateString('en-GB')}</td>
                )}
              />
              <GridColumn field="client" title="Client" width="200px" />
              <GridColumn field="site" title="Site" width="200px" />
              <GridColumn 
                field="value" 
                title="Total Value" 
                width="150px"
                cell={(props) => (
                  <td style={{ fontWeight: 'bold', textAlign: 'right' }}>
                    £{Number(props.dataItem.value).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </td>
                )}
              />
              <GridColumn 
                field="isPaid" 
                title="Status" 
                width="120px"
                cell={(props) => (
                  <td>
                    <span style={{
                      padding: '4px 8px',
                      borderRadius: '4px',
                      fontSize: '0.85rem',
                      fontWeight: 'bold',
                      backgroundColor: props.dataItem.isPaid ? '#d4edda' : '#f8d7da',
                      color: props.dataItem.isPaid ? '#155724' : '#721c24'
                    }}>
                      {props.dataItem.isPaid ? 'Paid' : 'Unpaid'}
                    </span>
                  </td>
                )}
              />
            </Grid>
          </div>
        )}

        {/* Empty State */}
        {!loading && data.length === 0 && (
          <div style={{ 
            textAlign: 'center', 
            padding: '4rem 2rem',
            color: '#666'
          }}>
            <p style={{ fontSize: '1.2rem', marginBottom: '0.5rem' }}>No invoices found</p>
            <p>Select a date range and click "Run Report" to view invoices</p>
          </div>
        )}
      </div>
    </div>
  )
}

export default InvoicesReport
